export const BASE_URL = 'https://power.ng';

export interface SeoRoute {
  path: string;
  title: string;
  description: string;
  changefreq: 'daily' | 'weekly' | 'monthly' | 'yearly';
  priority: string;
  type?: 'website' | 'tool' | 'legal' | 'lead';
  noPrerender?: boolean;
}

export const staticRoutes: SeoRoute[] = [
  {
    path: '/',
    title: "Power.ng - Nigeria's Energy Information Platform",
    description: "Detailed analysis, tools, and data about Nigeria's power and energy sector. Calculate bills, compare solar costs, track tariffs, and understand the grid.",
    changefreq: 'daily',
    priority: '1.0',
    type: 'website',
  },
  {
    path: '/grids',
    title: 'Nigeria Power Grid Stats & Tariff Band Information - Power.ng',
    description: "Track Nigeria's power grid, tariff bands, DisCos, grid collapses, and consumer rights.",
    changefreq: 'weekly',
    priority: '0.8',
    type: 'website',
  },
  {
    path: '/renewables',
    title: 'Renewable Energy & Solar Solutions in Nigeria - Power.ng',
    description: 'Compare solar costs, batteries, financing options, installers, and renewable energy opportunities in Nigeria.',
    changefreq: 'weekly',
    priority: '0.8',
    type: 'website',
  },
  {
    path: '/oil-gas',
    title: 'Oil, Gas & Refinery Updates in Nigeria - Power.ng',
    description: "Track Nigerian refinery operations, fuel prices, LPG trends, CNG adoption, and petroleum market analysis.",
    changefreq: 'weekly',
    priority: '0.8',
    type: 'website',
  },
  {
    path: '/tools',
    title: 'Energy Calculators & Tools for Nigeria - Power.ng',
    description: 'Free Nigerian energy tools for electricity bills, solar sizing, outage costs, tariff bands, energy audits, refineries, and DisCos.',
    changefreq: 'weekly',
    priority: '0.8',
    type: 'website',
  },
  {
    path: '/articles',
    title: 'Energy Insights & Analysis for Nigeria - Power.ng',
    description: "Latest explainers, guides, and analysis on Nigeria's power grid, solar market, tariffs, refineries, LPG, CNG, and energy policy.",
    changefreq: 'daily',
    priority: '0.9',
    type: 'website',
  },
  {
    path: '/solar-quotes',
    title: 'Get Solar Installation Quotes in Nigeria - Power.ng',
    description: 'Request quotes from verified Nigerian solar installers for homes, businesses, estates, and offices.',
    changefreq: 'monthly',
    priority: '0.8',
    type: 'lead',
  },
  {
    path: '/about',
    title: "About Power.ng - Nigeria's Energy Information Platform",
    description: "Learn about Power.ng's mission to help Nigerians make better electricity, solar, fuel, and energy decisions.",
    changefreq: 'monthly',
    priority: '0.5',
    type: 'website',
  },
  {
    path: '/contact',
    title: 'Contact Power.ng',
    description: 'Contact Power.ng for solar partnerships, advertising, data corrections, commercial inquiries, and platform feedback.',
    changefreq: 'monthly',
    priority: '0.5',
    type: 'website',
  },
  {
    path: '/data-sources',
    title: 'Power.ng Data Sources',
    description: "Review Power.ng's electricity, renewable energy, oil, gas, and energy-sector data sources.",
    changefreq: 'monthly',
    priority: '0.5',
    type: 'website',
  },
  {
    path: '/privacy-policy',
    title: 'Privacy Policy - Power.ng',
    description: 'Read the Power.ng Privacy Policy and learn how information is collected, used, and protected.',
    changefreq: 'yearly',
    priority: '0.3',
    type: 'legal',
  },
  {
    path: '/terms-of-use',
    title: 'Terms of Use - Power.ng',
    description: 'Read the Power.ng Terms of Use for using the platform, articles, tools, and data.',
    changefreq: 'yearly',
    priority: '0.3',
    type: 'legal',
  },
];

export const toolRoutes: SeoRoute[] = [
  {
    path: '/tools/bill-calculator',
    title: 'Electricity Bill Calculator Nigeria - Power.ng',
    description: 'Estimate your Nigerian electricity bill by kWh, tariff band, and DisCo using Power.ng.',
    changefreq: 'monthly',
    priority: '0.7',
    type: 'tool',
  },
  {
    path: '/tools/solar-sizing',
    title: 'Solar Sizing Calculator Nigeria - Power.ng',
    description: 'Estimate solar panel, inverter, battery, and installation needs for a Nigerian home or business.',
    changefreq: 'monthly',
    priority: '0.7',
    type: 'tool',
  },
  {
    path: '/power-manager',
    title: 'Power Manager Game - Power.ng',
    description: 'Play the Nigerian power management game and learn how grid, generator, and solar choices affect your budget.',
    changefreq: 'monthly',
    priority: '0.7',
    type: 'tool',
  },
  {
    path: '/tools/tariff-checker',
    title: 'Tariff Band Checker Nigeria - Power.ng',
    description: 'Estimate your electricity tariff band from your daily power supply hours and location.',
    changefreq: 'monthly',
    priority: '0.7',
    type: 'tool',
  },
  {
    path: '/tools/outage-calculator',
    title: 'Power Outage Cost Calculator Nigeria - Power.ng',
    description: 'Calculate generator fuel costs, outage losses, and solar payback opportunities for Nigerian homes and businesses.',
    changefreq: 'monthly',
    priority: '0.7',
    type: 'tool',
  },
  {
    path: '/tools/energy-audit',
    title: 'Home Energy Audit Nigeria - Power.ng',
    description: 'Run a quick energy efficiency audit and get practical Nigerian household power-saving recommendations.',
    changefreq: 'monthly',
    priority: '0.7',
    type: 'tool',
  },
  {
    path: '/tools/refinery-tracker',
    title: 'Nigeria Refinery Tracker - Power.ng',
    description: 'Track the status, capacity, and products of Nigerian refineries including Dangote, Port Harcourt, Warri, and modular refineries.',
    changefreq: 'monthly',
    priority: '0.7',
    type: 'tool',
  },
  {
    path: '/tools/disco-directory',
    title: 'Nigeria DisCo Directory - Power.ng',
    description: 'Find electricity distribution company contact numbers, emails, websites, and coverage areas across Nigeria.',
    changefreq: 'monthly',
    priority: '0.7',
    type: 'tool',
  },
];

export const explicitRedirects: Record<string, string> = {
  'http://www.power.ng/*': 'https://power.ng/:splat',
  'https://www.power.ng/*': 'https://power.ng/:splat',
  'http://power.ng/*': 'https://power.ng/:splat',
  'https://powerng.netlify.app/*': 'https://power.ng/:splat',
  '/articles/dangote-refinery-jobs-recruitment-2025': '/articles/dangote-refinery-jobs-recruitment',
  '/articles/how-to-apply-prepaid-electricity-meters': '/articles/prepaid-meter-application-nigeria-guide',
  '/articles/solar-vs-generator-cost-comparison-nigeria': '/articles/generator-vs-solar-cost-analysis',
};

export function toAbsoluteUrl(path: string): string {
  return path === '/' ? `${BASE_URL}/` : `${BASE_URL}${path}`;
}
